/** @jsx jsx */
import React, { type ReactElement, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { jsx, css, styled } from '@compiled/react';
import sortBy from 'lodash/sortBy';
import ReactFocusLock from 'react-focus-lock';
import Avatar from '@atlaskit/avatar';
import { IconTile } from '@atlaskit/icon';
import BranchIcon from '@atlaskit/icon/core/branch';
import AkSettingsIcon from '@atlaskit/icon/core/settings';
import SettingsIcon from '@atlaskit/icon/core/migration/settings';
import { BitbucketIcon } from '@atlaskit/logo';
import { LinkItem, Section } from '@atlaskit/menu';
import { Anchor, Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { layers, gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import type { DropdownTriggerParams } from '@atlassian/jira-development-summary-common/src/common/types.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { useIssueId, useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useIssueTypeField } from '@atlassian/jira-issue-field-issue-type/src/services/index.tsx';
import { useSummaryField } from '@atlassian/jira-issue-field-summary-common/src/services/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import {
	ContextualAnalyticsData,
	fireUIAnalytics,
	FireUiAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useProjectKey } from '@atlassian/jira-project-context-service/src/main.tsx';
import { DevSummaryCreateBranchPopupSkeleton } from '@atlassian/jira-skeletons/src/ui/dev-summary-create-branch-popup/DevSummaryCreateBranchPopupSkeleton.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { GIT_COMMAND_SECTION_TEST_ID } from '../../common/constants.tsx';
import type { CreateBranchTarget } from '../../common/types.tsx';
import { useBranchCommandBuilderStore } from '../../controllers/branch-command-builder-store/index.tsx';
import BranchCommandBuilder from './copy-branch-command/branch-command-builder/main.tsx';
import CopyBranchCommand from './copy-branch-command/main.tsx';
import messages from './messages.tsx';

const linkItemStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&&': {
		minHeight: 'initial',
	},
});

export type Props = {
	getDropdownTrigger: (props: DropdownTriggerParams) => ReactElement;
	createBranchTargets: CreateBranchTarget[];
	hideCreateBranchLinks?: boolean;
	onOpenDropdown?: (arg1: { isOpen: boolean }) => void;
};

const getAvatarSrc = (iconUrl: null | string, iconClass: null | string) => {
	if (iconUrl !== null) return <Avatar appearance="square" size="xsmall" src={iconUrl} />;

	if (iconClass === 'bitbucket') {
		return (
			<Box>
				<BitbucketIcon appearance="brand" size="small" />
			</Box>
		);
	}

	return (
		<Box xcss={boxStyles}>
			<IconTile icon={BranchIcon} label="" appearance="blueBold" shape="square" size="16" />
		</Box>
	);
};

export const useIsDropdownOpen = (isOpen = false) => useState(isOpen);

const CreateBranchDropdownInner = ({
	createBranchTargets,
	onOpenDropdown,
	getDropdownTrigger,
	hideCreateBranchLinks = false,
}: Props) => {
	const { formatMessage } = useIntl();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isOpen, setIsOpen] = useIsDropdownOpen();
	const [isBranchConfigOpen, setIsBranchConfigOpen] = useState(false);
	const previousOpen = useRef(false);
	const [{ isLoading }, { getUserConfigBranchData, setBranchData }] =
		useBranchCommandBuilderStore();
	const configButtonRef = useRef<HTMLButtonElement | null>(null);
	const accountId = useAccountId();
	const issueKey = useIssueKey();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const issueId = fg('relay-migration-issue-fields-summary') ? useIssueId() : undefined;
	const [{ value: issueType }] = useIssueTypeField({ issueKey });
	const [{ value: issueSummary }] = useSummaryField({ issueKey, issueId });
	const projectKey = useProjectKey(issueKey);
	const menuGroupRef = useRef<HTMLDivElement>(null);
	const [activeItemId, setActiveItemId] = useState<string | null>(null);

	const handleOnClick = () => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'updateBranchNameFormatButton',
		});

		fireUIAnalytics(analyticsEvent);

		setIsBranchConfigOpen(true);
	};

	const handleOnHover = () => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'hovered',
			actionSubject: 'updateBranchNameFormatButton',
		});

		fireUIAnalytics(analyticsEvent);
	};

	useEffect(() => {
		if (previousOpen.current !== isOpen) {
			previousOpen.current = isOpen;
			if (isOpen) {
				fireUIAnalytics(createAnalyticsEvent({}), 'dropdown opened', 'createBranchDropdown');
				setBranchData({
					issueKey,
					issueSummary: issueSummary || '',
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					issueType: (issueType?.name as string) || '',
					projectKey,
				});
				getUserConfigBranchData(accountId);

				setTimeout(() => {
					if (!isBranchConfigOpen && menuGroupRef.current && fg('dev_details_add_focus_to_list')) {
						const firstMenuItem =
							menuGroupRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
						if (firstMenuItem) {
							firstMenuItem.focus();
							setActiveItemId(firstMenuItem.getAttribute('data-testid'));
						}
					}
				}, 0);
			} else {
				fireUIAnalytics(createAnalyticsEvent({}), 'dropdown closed', 'createBranchDropdown');
				isBranchConfigOpen && setIsBranchConfigOpen(false);
			}
			onOpenDropdown && onOpenDropdown({ isOpen });
		}
	}, [
		createAnalyticsEvent,
		createBranchTargets,
		isOpen,
		onOpenDropdown,
		accountId,
		getUserConfigBranchData,
		setBranchData,
		issueKey,
		issueSummary,
		issueType?.name,
		projectKey,
		isBranchConfigOpen,
	]);

	const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
		if (!fg('dev_details_add_focus_to_list')) return;
		if (!menuGroupRef.current) return;

		const menuItems: HTMLElement[] = Array.from(
			menuGroupRef.current.querySelectorAll<HTMLElement>('[role="menuitem"]'),
		);
		if (menuItems.length === 0) return;

		const currentIndex = menuItems.findIndex((item) => item === globalThis.document?.activeElement);

		switch (event.key) {
			case 'ArrowDown': {
				event.preventDefault();
				const nextIndex = currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0;
				menuItems[nextIndex].focus();
				// Update active item tracking using data-testid
				setActiveItemId(menuItems[nextIndex].getAttribute('data-testid') || null);
				break;
			}
			case 'ArrowUp': {
				event.preventDefault();
				const prevIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1;
				menuItems[prevIndex].focus();
				// Update active item tracking using data-testid
				setActiveItemId(menuItems[prevIndex].getAttribute('data-testid') || null);
				break;
			}
			default: {
				break;
			}
		}
	}, []);

	useEffect(() => {
		if (!isBranchConfigOpen && configButtonRef.current) {
			configButtonRef.current.focus();
		}
	}, [isBranchConfigOpen]);
	const createBranchDropdownItems = useMemo(
		() =>
			sortBy(createBranchTargets, 'name').map(
				({ iconClass, iconUrl, name: remoteAppName, applicationType, url: remoteAppUrl }, index) =>
					remoteAppUrl !== null &&
					remoteAppUrl !== undefined &&
					(fg('dev_details_add_focus_to_list') ? (
						<Box xcss={listStyles} as="li" role="none" key={index}>
							<Anchor
								key={remoteAppUrl}
								href={remoteAppUrl}
								role="menuitem"
								tabIndex={-1}
								onClick={() => {
									setIsOpen(false);
									fireUIAnalytics(createAnalyticsEvent({}), 'link clicked', 'scmCreateBranchLink', {
										redirectToScmProviderPage: true,
										scmProviderType: applicationType,
									});
								}}
								target="_blank"
								rel="noreferrer noopener"
								id={`development-summary-branch.ui.create-branch-dropdown.link-item--${index}`}
								testId={`development-summary-branch.ui.create-branch-dropdown.link-item--${index}`}
								xcss={anchorStyles}
							>
								<Box as="span" xcss={avatarStyles}>
									<Box>{getAvatarSrc(iconUrl, iconClass)}</Box>
									<Box xcss={labelStyles}>
										{formatMessage(messages.createBranchMessage, { remoteAppName })}
									</Box>
								</Box>
							</Anchor>
						</Box>
					) : (
						<LinkItem
							css={linkItemStyles}
							key={remoteAppUrl}
							href={remoteAppUrl}
							onClick={() => {
								setIsOpen(false);
								fireUIAnalytics(createAnalyticsEvent({}), 'link clicked', 'scmCreateBranchLink', {
									redirectToScmProviderPage: true,
									scmProviderType: applicationType,
								});
							}}
							iconBefore={getAvatarSrc(iconUrl, iconClass)}
							target="_blank"
							rel="noreferrer noopener"
							testId="development-summary-branch.ui.create-branch-dropdown.link-item"
						>
							{formatMessage(messages.createBranchMessage, { remoteAppName })}
						</LinkItem>
					)),
			),
		[createBranchTargets, formatMessage, createAnalyticsEvent, setIsOpen],
	);

	const showCreateBranchLinkSection = !hideCreateBranchLinks && createBranchTargets.length > 0;

	return (
		<Popup
			id="create-branch-dropdown"
			zIndex={layers.modal + 1}
			placement="bottom-end"
			shouldFlip
			messageId="development-summary-branch.ui.create-branch-dropdown.popup"
			messageType="transactional"
			trigger={(triggerProps) =>
				getDropdownTrigger({
					...triggerProps,
					isSelected: isOpen,
					onClick: () => setIsOpen(!isOpen),
				})
			}
			isOpen={isOpen}
			onClose={() => {
				setIsOpen(false);
				setIsBranchConfigOpen(false);
			}}
			content={() => (
				<BranchDropdownWidthContainer data-testid="development-summary-branch.ui.create-branch-dropdown.branch-dropdown-width-container">
					{isBranchConfigOpen ? (
						<BranchCommandBuilder setIsBranchConfigOpen={setIsBranchConfigOpen} />
					) : (
						<ReactFocusLock returnFocus>
							{showCreateBranchLinkSection && (
								<Box
									ref={menuGroupRef}
									role="menu"
									testId="development-summary-branch.ui.create-branch-dropdown.menu-group"
								>
									<Section
										aria-activedescendant={activeItemId || undefined}
										testId="development-summary-branch.ui.create-branch-dropdown.create-source-branch-section"
										title={formatMessage(messages.branchTargetsGroupTitle)}
									>
										<BranchDropdownScrollContainer
											tabIndex={0}
											role="menu"
											onKeyDown={handleKeyDown}
										>
											{createBranchDropdownItems}
										</BranchDropdownScrollContainer>
										<FireUiAnalytics action="shown" actionSubject="createBranchLinkList" />
									</Section>
								</Box>
							)}
							<Section
								hasSeparator={showCreateBranchLinkSection}
								testId={GIT_COMMAND_SECTION_TEST_ID}
							>
								{isLoading ? (
									<DevSummaryCreateBranchPopupSkeleton />
								) : (
									<>
										<GitCommandSectionHeader>
											<GitCommandTitle data-testid="development-summary-branch.ui.create-branch-dropdown.git-command-title">
												{formatMessage(messages.gitCommandGroupTitle)}
											</GitCommandTitle>
											<Tooltip
												content={formatMessage(messages.configurableBranchCreateButtonLabel)}
												position="top"
												testId="development-summary-branch.ui.create-branch-dropdown.tooltip"
											>
												<ConfigButton
													showCreateBranchLinkSection
													ref={configButtonRef}
													onClick={handleOnClick}
													onMouseEnter={handleOnHover}
													data-testid="development-summary-branch.ui.create-branch-dropdown.config-button"
												>
													{isVisualRefreshEnabled() ? (
														<AkSettingsIcon
															color={token('color.icon')}
															label={formatMessage(messages.configurableBranchCreateButtonLabel)}
														/>
													) : (
														<SettingsIcon
															label={formatMessage(messages.configurableBranchCreateButtonLabel)}
															LEGACY_size="small"
														/>
													)}
												</ConfigButton>
											</Tooltip>
										</GitCommandSectionHeader>
										<FauxDropdownItem>
											<CopyBranchCommand />
										</FauxDropdownItem>
									</>
								)}
							</Section>
						</ReactFocusLock>
					)}
				</BranchDropdownWidthContainer>
			)}
		/>
	);
};

export const CreateBranchDropdown = ({
	createBranchTargets,
	onOpenDropdown,
	getDropdownTrigger,
	hideCreateBranchLinks = false,
}: Props) => {
	const scmProviderTypes = useMemo(
		() => createBranchTargets.map((target) => target.applicationType),
		[createBranchTargets],
	);

	return (
		<ContextualAnalyticsData
			attributes={{
				scmProviderTypes,
				singleScmProvider: createBranchTargets.length === 1,
			}}
		>
			<CreateBranchDropdownInner
				createBranchTargets={createBranchTargets}
				onOpenDropdown={onOpenDropdown}
				getDropdownTrigger={getDropdownTrigger}
				hideCreateBranchLinks={hideCreateBranchLinks}
			/>
		</ContextualAnalyticsData>
	);
};

export default CreateBranchDropdown;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FauxDropdownItem = styled.div({
	paddingTop: token('space.050'),
	paddingRight: token('space.250'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.250'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BranchDropdownScrollContainer = styled.div({
	maxHeight: '145px',
	overflow: 'auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BranchDropdownWidthContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 48}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ConfigButton = styled.button<{ showCreateBranchLinkSection: Boolean }>({
	paddingTop: token('space.050'),
	paddingRight: token('space.050'),
	paddingBottom: token('space.050'),
	paddingLeft: token('space.050'),
	border: 'none',
	borderRadius: '4px',
	backgroundColor: 'transparent',
	cursor: 'pointer',
	'&:hover': {
		backgroundColor: token('color.background.neutral.hovered'),
		transition: 'background-color 0.1s ease-out',
	},
	transition: 'all 0.1s ease-out',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GitCommandSectionHeader = styled.div({
	paddingTop: 0,
	paddingRight: token('space.250'),
	paddingBottom: 0,
	paddingLeft: token('space.250'),
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GitCommandTitle = styled.div({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	textTransform: 'uppercase',

	font: token('font.body.small'),
	fontFamily: token('font.family.body'),
	fontWeight: token('font.weight.bold'),
	marginTop: token('space.100'),
	marginRight: 0,
	marginBottom: token('space.100'),
	marginLeft: 0,

	color: token('color.text.subtle'),
});

const boxStyles = xcss({
	backgroundColor: 'elevation.surface.overlay',
	height: token('space.250'),
	width: token('space.250'),
	borderRadius: 'border.radius.100',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

const anchorStyles = xcss({
	display: 'flex',
	flexWrap: 'nowrap',
	color: 'color.text',
	backgroundColor: 'elevation.surface',
	textDecoration: 'none',
	paddingInline: 'space.200',
	paddingBlock: 'space.100',
	margin: 'space.050',
	':hover': {
		backgroundColor: 'elevation.surface.hovered',
		textDecoration: 'none',
	},
	':active': {
		backgroundColor: 'elevation.surface.pressed',
		textDecoration: 'none',
	},
});

const listStyles = xcss({
	listStyle: 'none',
	padding: '0',
});

const avatarStyles = xcss({
	alignItems: 'center',
	display: 'flex',
	flexDirection: 'row',
	gap: 'space.150',
	width: '100%',
});

const labelStyles = xcss({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});
